import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonBase, InputBase, Stack } from '@mui/material';
import { ArrowRight, CornerDownRight, Pen } from 'lucide-react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { PendingAskUser } from '../../hooks/usePendingAskUser';
import { useRachelAskUserCard } from './useRachelAskUserCard';

const ROW_HEIGHT_SPACING = 3.5;
const PEN_SLOT_SPACING = 2.5;
const ARROW_SLOT_SPACING = 1.5;

const RachelAskUserCard = ({ toolCallId, question, options }: PendingAskUser) => {
  const { translate } = useTranslation();
  const { otherText, isDisabled, setOtherText, selectOption, skip, handleOtherKeyDown } =
    useRachelAskUserCard({ toolCallId });
  const otherLabel = translate(LangEnum.RACHEL_ASK_USER_OTHER);

  return (
    <Stack
      test-id="rachel-ask-user-card"
      onMouseDown={(event) => event.stopPropagation()}
      sx={(theme) => ({
        boxSizing: 'border-box',
        width: '100%',
        gap: 1,
        p: 1,
        overflow: 'hidden',
        borderRadius: `${theme.shape.borderRadiusLg}px`,
        border: `1px solid ${theme.palette.custom.border.secondary}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[8],
      })}
    >
      <Box sx={{ py: 0.25, px: 0.5 }}>
        <Typography
          variant="body2"
          color="text.primary"
          sx={{ pl: 0.25, textTransform: 'lowercase', overflowWrap: 'anywhere' }}
        >
          {question}
        </Typography>
      </Box>
      <Stack gap={0.5}>
        {options.map((option, index) => (
          <ButtonBase
            key={`${index}-${option}`}
            test-id={`rachel-ask-user-option-${index}`}
            disabled={isDisabled}
            onClick={() => selectOption(option)}
            sx={(theme) => ({
              font: 'inherit',
              justifyContent: 'flex-start',
              gap: 0.75,
              height: theme.spacing(ROW_HEIGHT_SPACING),
              p: 1,
              borderRadius: `${theme.shape.borderRadiusSm}px`,
              textAlign: 'left',
              '&:hover, &.Mui-focusVisible': { backgroundColor: theme.palette.action.hover },
              '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.light}` },
            })}
          >
            <Box component="span" sx={{ display: 'flex', flex: 'none', color: 'primary.main' }}>
              <Icon icon={CornerDownRight} size="xs" />
            </Box>
            <Typography
              variant="body2"
              component="span"
              color="text.secondary"
              noWrap
              sx={{ px: 0.25, minWidth: 0 }}
            >
              {option}
            </Typography>
          </ButtonBase>
        ))}
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack
            flex={1}
            minWidth={0}
            direction="row"
            alignItems="center"
            gap={0.25}
            sx={(theme) => ({
              height: theme.spacing(ROW_HEIGHT_SPACING),
              pl: 0.5,
              pr: 1,
              borderRadius: `${theme.shape.borderRadiusLg}px`,
              '&:hover, &:focus-within': { backgroundColor: theme.palette.action.hover },
            })}
          >
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: theme.spacing(PEN_SLOT_SPACING),
                height: theme.spacing(PEN_SLOT_SPACING),
                flex: 'none',
                color: 'text.tertiary',
              })}
            >
              <Icon icon={Pen} size="sm" />
            </Box>
            <InputBase
              fullWidth
              disabled={isDisabled}
              value={otherText}
              placeholder={otherLabel}
              inputProps={{ 'aria-label': otherLabel }}
              onChange={(event) => setOtherText(event.target.value)}
              onKeyDown={handleOtherKeyDown}
              sx={(theme) => ({
                ...theme.typography.body2,
                '& input': {
                  p: 0,
                  px: 0.25,
                  '&::placeholder': { color: 'text.disabled', opacity: 1 },
                },
              })}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={(theme) => ({ height: theme.spacing(ROW_HEIGHT_SPACING), px: 0.5 })}
          >
            <ButtonBase
              test-id="rachel-ask-user-skip"
              disabled={isDisabled}
              onClick={skip}
              sx={(theme) => ({
                gap: 0.25,
                py: 0.25,
                pr: 0.5,
                pl: 0.75,
                borderRadius: `${theme.shape.borderRadiusMd}px`,
                border: `1px solid ${theme.palette.custom.border.secondary}`,
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.light}` },
              })}
            >
              <Typography variant="micro1" component="span" color="inherit">
                {translate(LangEnum.GLOBAL_SKIP_BUTTON_LABEL)}
              </Typography>
              <Box
                component="span"
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: theme.spacing(ARROW_SLOT_SPACING),
                  height: theme.spacing(ARROW_SLOT_SPACING),
                })}
              >
                <Icon icon={ArrowRight} size="xs" />
              </Box>
            </ButtonBase>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default RachelAskUserCard;
